import React, { useMemo } from 'react';
import Box from 'Components/Atoms/Box';
import { CurrentStatus, Reservation } from 'types/reservations';
import './styles.scss';
import Typography from 'Components/Atoms/Typography';
import IconButton from 'Components/Atoms/IconButton';
import { CheckCircle, Seat } from 'Components/Atoms/Icons';
import { blockToTime, classHelper, noop, timeToBlock } from 'utils/helper';
import useRestaurant from 'CustomHooks/useRestaurant';
import { ReservationActionTypes } from 'Contexts/ReservationContext';

export type SmallReservationCardProps = {
  reservation: Reservation;
  handleAction: (
    id: string,
    type: ReservationActionTypes,
    payload?: any,
  ) => void;
  /** Blocks the card shows when the schedule cuts it at its edge. */
  visibleLength?: number;
  continuesBefore?: boolean;
  continuesAfter?: boolean;
};

const SmallReservationCard = ({
  reservation,
  handleAction = noop,
  visibleLength,
  continuesBefore = false,
  continuesAfter = false,
}: SmallReservationCardProps) => {
  const {
    id = '',
    currentStatus = '',
    time = '',
    guests = 0,
    guest,
    reservationLength = 4,
  } = reservation;

  const { reservationSettings } = useRestaurant();

  const coloredLogo = useMemo(() => {
    return ['normal', 'done', 'failed'].includes(currentStatus);
  }, [currentStatus]);

  const resaHeight = useMemo(() => {
    return (visibleLength ?? reservationLength) * 12;
  }, [visibleLength, reservationLength]);

  // Two lines plus padding need an hour of height; below that the card would
  // clip both of them and show an empty middle instead.
  const oneLine = resaHeight < 48;

  return (
    <Box
      outline
      elevation={0}
      className={classHelper([
        `resa-${currentStatus} reservation-card space-between small-card`,
        continuesBefore && 'continues-before',
        continuesAfter && 'continues-after',
      ])}
      style={{ padding: oneLine ? '2px 8px' : '6px 8px', height: resaHeight }}
      fullSize
    >
      {continuesBefore && <span className="cut-edge top" />}
      {continuesAfter && <span className="cut-edge bottom" />}
      <Box
        className={oneLine ? 'flex flex-start' : 'flex-column flex-start'}
        style={
          oneLine ? { gap: 6, alignItems: 'baseline', minWidth: 0 } : undefined
        }
      >
        <Typography
          variant="text-5"
          className={coloredLogo ? 'text-subdued' : 'text-white'}
          style={oneLine ? { whiteSpace: 'nowrap' } : undefined}
        >
          {reservationSettings?.showReservationLength
            ? `${time} - ${blockToTime(timeToBlock(time) + reservationLength)}`
            : time}{' '}
          • {guests} Pax
        </Typography>
        <Typography
          variant="text-3"
          className={coloredLogo ? '' : 'text-white'}
          style={
            oneLine
              ? {
                  flex: '1 1 auto',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }
              : undefined
          }
        >
          {guest?.name || ''}
        </Typography>
      </Box>
      <Box style={{ width: 'fit-content' }}>
        {currentStatus === 'seated' ||
        currentStatus === CurrentStatus.HAS_ORDERED ||
        currentStatus === CurrentStatus.PAID ? (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleAction(id, ReservationActionTypes.MARK_AS_DONE);
            }}
            size="large"
            className={coloredLogo ? ' text-subdued' : ' text-white'}
          >
            <CheckCircle />
          </IconButton>
        ) : (
          currentStatus !== 'done' && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleAction(id, ReservationActionTypes.SEAT);
              }}
              size="small"
              className={coloredLogo ? ' text-subdued' : ' text-white'}
            >
              <Seat />
            </IconButton>
          )
        )}
      </Box>
    </Box>
  );
};

export default SmallReservationCard;
